import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Minus, Plus, Trash2, Lock, Gift, Check, PackageOpen, Loader2, Heart, Sparkles, ShoppingBag, Star } from "lucide-react";
import { auth } from "../../firebase";
import { AppDispatch, RootState } from "../../redux/store";
import { clearCart } from "../../redux/cartSlice";
import { listenToPublishedProducts } from "../../redux/productSlice";

const CHECKOUT_URL = process.env.REACT_APP_CHECKOUT_URL || "https://beautables.app/api/Checkout";

/* ═══════════════ what the shop sends, and what the cart store holds, in one shape ═══════════════ */
type Line = {
  key: string; productId: string; ownerId: string; name: string;
  price: number; qty: number; photo: string; color?: string; size?: string;
};
const lineKey = (i: any) => `${i.productId}|${i.color ?? ""}|${i.size ?? ""}`;
const toLines = (raw: any[]): Line[] =>
  (raw || []).map((i) => ({
    key: lineKey(i),
    productId: String(i.productId ?? i.listingId ?? ""),
    ownerId: String(i.ownerId ?? ""),
    name: i.name ?? "",
    price: Number(i.price ?? i.rate) || 0,
    qty: Math.max(1, Number(i.quantity ?? i.qty) || 1),
    photo: i.photo ?? "",
    color: i.color || undefined,
    size: i.size || undefined,
  }));

type Gift = {
  firstName: string; lastName: string; email: string; phone: string;
  address: string; city: string; state: string; postalCode: string; country: string;
};
const emptyGift = (): Gift => ({ firstName: "", lastName: "", email: "", phone: "", address: "", city: "", state: "", postalCode: "", country: "" });

const money = (v: number) => `€${(Number(v) || 0).toFixed(2)}`;

/* ═══════════════ STYLES: same palette as the shop (black, white, yellow, raspberry, lime, pink), turned up to cute ═══════════════ */
const INK = "#000";
const RED = "#E10000";
const RED_DARK = "#B30000";
const BODY = "#5C5C5C";
const LINE = "#F3D3E4";
const YEL = "#FFD60A";
const YEL_HI = "#FFF27A";
const YEL_LO = "#F2B800";
const YEL_EDGE = "#C99700";
const YEL_SOFT = "#FFF8CC";
const RASP = "#E30B5C";
const LIME = "#C8E17A";
const PINK = "#FF6BB5";
const PINK_SOFT = "#FFF0F7";
const PINK_MID = "#FFD0E6";
const YEL_GLOSS = `linear-gradient(180deg, ${YEL_HI} 0%, ${YEL} 52%, ${YEL_LO} 100%)`;
const RASP_GLOSS = `linear-gradient(180deg, #FF5C95 0%, ${RASP} 52%, #B80848 100%)`;
const LIME_GLOSS = `linear-gradient(180deg, #E6F5B4 0%, ${LIME} 52%, #A9C95A 100%)`;
const PINK_GLOSS = `linear-gradient(180deg, #ffb3da 0%, ${PINK} 55%, #f0509f 100%)`;

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@500;600;700&family=Jost:wght@300;400;500;600&display=swap');
.ck-page, .ck-page *, .ck-page *::before, .ck-page *::after{ box-sizing:border-box; font-family:'Jost',system-ui,sans-serif; }
.ck-page{ position:relative; min-height:100vh; color:${INK}; overflow-x:hidden;
  background-color:${PINK_SOFT};
  background-image:radial-gradient(${PINK_MID} 2.2px, transparent 2.4px), radial-gradient(${YEL_SOFT} 2.2px, transparent 2.4px);
  background-size:26px 26px, 26px 26px; background-position:0 0, 13px 13px;
  padding:24px 16px 120px; padding-top:calc(24px + env(safe-area-inset-top)); padding-bottom:calc(120px + env(safe-area-inset-bottom)); }
.ck-page button{ font-family:inherit; cursor:pointer; border:none; background:none; color:inherit; }
.ck-page button:disabled{ cursor:not-allowed; opacity:.5; }
.ck-page button:focus-visible, .ck-page input:focus-visible{ outline:3px solid ${INK}; outline-offset:3px; }
.ck-wrap{ position:relative; z-index:1; max-width:640px; margin:0 auto; }
.ck-page h2{ font-family:'Cormorant Garamond',serif; font-size:clamp(42px,8vw,58px); font-weight:700; line-height:1; margin:10px 0 6px; display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.ck-page h3{ font-family:'Cormorant Garamond',serif; font-size:30px; font-weight:700; margin:0; }
.ck-page p{ margin:0; }
.ck-muted{ color:${BODY}; }
.ck-small{ font-size:13px; }
.ck-sub{ display:flex; align-items:center; gap:7px; margin:0 0 22px; font-size:12px; font-weight:500; letter-spacing:.2em; text-transform:uppercase; color:${RASP}; }

/* floating sparkles */
.ck-fl{ position:absolute; z-index:0; pointer-events:none; opacity:.9; animation:ckFloat 5s ease-in-out infinite; }
.ck-fl.a{ top:90px; left:5%; color:${PINK}; }
.ck-fl.b{ top:170px; right:6%; color:${YEL_LO}; animation-delay:-1.4s; }
.ck-fl.c{ top:46%; left:3%; color:${RASP}; animation-delay:-2.6s; }
.ck-fl.d{ top:60%; right:4%; color:#A9C95A; animation-delay:-3.4s; }
@media (max-width:760px){ .ck-fl{ display:none; } }
@keyframes ckFloat{ 0%,100%{ transform:translateY(0) rotate(-8deg); } 50%{ transform:translateY(-14px) rotate(10deg); } }

.ck-back{ display:inline-flex; align-items:center; gap:8px; height:40px; padding:0 18px 0 12px; border-radius:999px; border:2px solid ${INK};
  background:linear-gradient(180deg,#fff,#f4f4f4); box-shadow:inset 0 2px 1px #fff, 0 4px 0 ${INK};
  font-size:11.5px; font-weight:600; letter-spacing:.18em; text-transform:uppercase; transition:all .2s; }
.ck-back:hover{ background:${YEL_GLOSS}; transform:translateY(-2px); box-shadow:inset 0 2px 1px rgba(255,255,255,.9), 0 6px 0 ${INK}; }
.ck-back:active{ transform:translateY(3px); box-shadow:0 1px 0 ${INK}; }

/* sticker cards */
.ck-card{ position:relative; background:#fff; border:2.5px solid ${INK}; border-radius:30px; padding:22px 20px; margin-bottom:22px;
  box-shadow:inset 0 3px 0 #fff, 0 7px 0 ${INK}, 0 20px 26px -14px rgba(0,0,0,.4); }
.ck-card::after{ content:""; position:absolute; inset:6px; border:2px dashed ${PINK_MID}; border-radius:24px; pointer-events:none; }
.ck-card > *{ position:relative; z-index:1; }
.ck-card-head{ display:flex; align-items:center; gap:10px; margin:-2px 0 14px; }
.ck-step{ width:34px; height:34px; border-radius:50%; flex:none; display:flex; align-items:center; justify-content:center; font-size:15px; font-weight:600; color:#fff;
  background:radial-gradient(circle at 30% 24%, #fff 0, rgba(255,255,255,.85) 6%, rgba(255,255,255,0) 26%), radial-gradient(circle at 36% 30%, #ff8cc4 0, ${PINK} 48%, #d63f8c 100%);
  box-shadow:inset -3px -4px 7px rgba(0,0,0,.25), inset 1px 1px 3px rgba(255,255,255,.6), 0 0 0 2px ${INK}, 0 4px 0 ${INK}; }
.ck-step.y{ color:${INK}; background:radial-gradient(circle at 30% 24%, #fff 0, rgba(255,255,255,.85) 6%, rgba(255,255,255,0) 26%), radial-gradient(circle at 36% 30%, ${YEL_HI} 0, ${YEL} 48%, ${YEL_LO} 100%); }
.ck-step.l{ color:${INK}; background:radial-gradient(circle at 30% 24%, #fff 0, rgba(255,255,255,.85) 6%, rgba(255,255,255,0) 26%), radial-gradient(circle at 36% 30%, #eaf8bd 0, ${LIME} 48%, #9bbd4f 100%); }
.ck-card-title{ font-family:'Cormorant Garamond',serif; font-size:26px; font-weight:700; line-height:1; }

/* lines */
.ck-line{ display:flex; align-items:center; gap:14px; padding:14px 0; border-bottom:2px dotted ${LINE}; }
.ck-line:last-child{ border-bottom:none; padding-bottom:4px; }
.ck-thumb{ width:72px; height:90px; border-radius:20px; overflow:hidden; background:${YEL_SOFT}; border:2px solid ${INK}; box-shadow:0 4px 0 ${INK}; flex-shrink:0; display:flex; align-items:center; justify-content:center; color:#6B6B6B; cursor:zoom-in;
  transition:transform .3s cubic-bezier(.2,.9,.3,1.5); }
.ck-thumb:hover{ transform:rotate(-4deg) scale(1.08); }
.ck-thumb img{ width:100%; height:100%; object-fit:cover; display:block; }
.ck-grow{ flex:1; min-width:0; }
.ck-name{ display:block; font-family:'Cormorant Garamond',serif; font-size:23px; font-weight:700; line-height:1.15; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.ck-opts{ display:flex; flex-wrap:wrap; gap:6px; margin-top:6px; }
.ck-opt{ font-size:10px; font-weight:600; letter-spacing:.14em; text-transform:uppercase; padding:4px 11px; border-radius:999px; border:1.5px solid ${INK}; background:${YEL_SOFT}; box-shadow:0 2px 0 ${INK}; }
.ck-opt:nth-child(2){ background:#EEF8D2; }
.ck-each{ font-size:13px; color:${BODY}; margin-top:7px; }
.ck-right{ display:flex; flex-direction:column; align-items:flex-end; gap:8px; flex-shrink:0; }
.ck-lt{ display:inline-flex; padding:3px 12px; border-radius:999px; background:linear-gradient(180deg,#fff,#ececec); border:1.5px solid ${INK}; box-shadow:0 3px 0 ${INK}; font-size:15px; font-weight:600; }
.ck-qty{ display:inline-flex; align-items:center; height:38px; padding:0 3px; border:2px solid ${INK}; border-radius:999px; background:#fff; box-shadow:0 3px 0 ${INK}; }
.ck-qty button{ width:30px; height:30px; border-radius:50%; display:flex; align-items:center; justify-content:center; transition:background .2s, transform .2s; }
.ck-qty button:hover:not(:disabled){ background:${LIME_GLOSS}; transform:scale(1.12); }
.ck-qty b{ min-width:26px; text-align:center; font-size:15px; font-weight:600; }
.ck-x{ width:32px; height:32px; border-radius:50%; border:2px solid ${INK}; background:#fff; display:flex; align-items:center; justify-content:center; box-shadow:0 3px 0 ${INK}; transition:all .2s; }
.ck-x:hover{ background:${RASP_GLOSS}; color:#fff; transform:rotate(-12deg) scale(1.1); }

/* gift switch */
.ck-gift{ display:flex; align-items:center; gap:14px; width:100%; text-align:left; padding:14px 16px; border-radius:22px; border:2px solid ${INK}; background:linear-gradient(180deg,#fff,#f6f6f6); box-shadow:0 4px 0 ${INK}; transition:all .2s; }
.ck-gift:hover{ background:${YEL_SOFT}; transform:translateY(-2px); box-shadow:0 6px 0 ${INK}; }
.ck-gift.on{ background:${PINK_SOFT}; }
.ck-orb{ position:relative; width:44px; height:44px; border-radius:50%; flex-shrink:0; display:flex; align-items:center; justify-content:center; color:#fff;
  background:radial-gradient(circle at 30% 24%, #fff 0, rgba(255,255,255,.85) 6%, rgba(255,255,255,0) 26%), radial-gradient(circle at 36% 30%, #ff6fa3 0, ${RASP} 48%, #8F0636 100%);
  box-shadow:inset -3px -4px 7px rgba(0,0,0,.32), inset 1px 1px 3px rgba(255,255,255,.6), 0 0 0 2px ${INK}, 0 5px 0 ${INK}; }
.ck-gift:hover .ck-orb{ animation:ckWiggle .6s ease-in-out; }
.ck-gift b{ display:block; font-size:12px; font-weight:600; letter-spacing:.18em; text-transform:uppercase; }
.ck-gift span.sub{ display:block; font-size:13px; color:${BODY}; margin-top:2px; }
.ck-knob{ position:relative; width:52px; height:30px; border-radius:999px; background:#E2E2E2; border:2px solid ${INK}; flex-shrink:0; margin-left:auto; transition:background .2s; }
.ck-knob::after{ content:""; position:absolute; top:2px; left:2px; width:22px; height:22px; border-radius:50%; background:#fff; border:2px solid ${INK}; transition:transform .25s cubic-bezier(.2,.9,.3,1.5); }
.ck-gift.on .ck-knob{ background:${PINK}; }
.ck-gift.on .ck-knob::after{ transform:translateX(22px); }

.ck-form{ display:grid; grid-template-columns:1fr 1fr; gap:0 14px; margin-top:6px; animation:ckDrop .3s cubic-bezier(.2,.9,.3,1.2); }
.ck-form .full{ grid-column:1 / -1; }
@media (max-width:520px){ .ck-form{ grid-template-columns:1fr; } }
.ck-label{ display:block; font-size:11px; font-weight:600; letter-spacing:.16em; text-transform:uppercase; margin:16px 0 8px; }
.ck-label i{ color:${RASP}; font-style:normal; margin-left:4px; }
.ck-input{ width:100%; height:48px; padding:0 18px; border-radius:999px; border:2px solid ${INK}; background:#fff; font-size:15px; color:${INK}; outline:none; box-shadow:inset 0 2px 4px rgba(0,0,0,.06), 0 3px 0 ${INK}; transition:all .2s; }
.ck-input::placeholder{ color:#B5A3AD; }
.ck-input:hover{ background:${YEL_SOFT}; }
.ck-input:focus{ background:${YEL_SOFT}; box-shadow:inset 0 2px 4px rgba(0,0,0,.06), 0 0 0 4px ${YEL}, 0 0 0 6px ${INK}; }
.ck-input.bad{ border-color:${RASP}; background:#FFE9F1; box-shadow:inset 0 2px 4px rgba(0,0,0,.06), 0 3px 0 ${RASP}; animation:ckShake .35s; }

/* totals */
.ck-row{ display:flex; align-items:baseline; justify-content:space-between; padding:6px 0; font-size:14px; }
.ck-total{ display:flex; align-items:center; justify-content:space-between; margin-top:12px; padding:12px 18px; border-radius:999px; background:${PINK_SOFT}; border:2px dashed ${PINK}; }
.ck-total small{ font-size:11px; font-weight:600; letter-spacing:.22em; text-transform:uppercase; color:${RASP}; }
.ck-total b{ font-family:'Cormorant Garamond',serif; font-size:38px; font-weight:700; line-height:1; }
.ck-error{ margin-top:14px; padding:10px 16px; border-radius:18px; border:2px solid ${INK}; background:#FFE9F1; box-shadow:0 3px 0 ${RASP}; font-size:13px; font-weight:500; color:${RED_DARK}; animation:ckShake .35s; }
.ck-note{ display:flex; align-items:center; gap:8px; margin:0 0 20px; padding:12px 18px; border-radius:999px; border:2px solid ${INK}; background:${YEL_SOFT}; box-shadow:0 4px 0 ${INK}; font-size:12px; font-weight:600; letter-spacing:.1em; text-transform:uppercase; }

/* glossy 3D button, same as the shop */
.ck-btn{ position:relative; isolation:isolate; overflow:hidden; display:inline-flex; align-items:center; justify-content:center; gap:8px; height:54px; padding:0 30px; border-radius:999px;
  background:linear-gradient(180deg,#4a4a4a 0%,#111 48%,#000 100%); border:1px solid #000; color:#fff !important;
  font-size:11.5px; font-weight:600; letter-spacing:.22em; text-transform:uppercase; text-shadow:0 1px 2px rgba(0,0,0,.5);
  box-shadow:inset 0 2px 2px rgba(255,255,255,.4), inset 0 -4px 6px rgba(0,0,0,.55), 0 5px 0 #2b2b2b, 0 12px 18px -6px rgba(0,0,0,.55);
  transition:background .25s, transform .15s, box-shadow .15s; }
.ck-btn::before{ content:""; position:absolute; z-index:-1; left:7%; right:7%; top:3px; height:46%; border-radius:999px; background:linear-gradient(180deg,rgba(255,255,255,.55),rgba(255,255,255,.05)); pointer-events:none; }
.ck-btn::after{ content:""; position:absolute; z-index:0; inset:0; pointer-events:none; transform:translateX(-130%); transition:transform .6s ease;
  background:linear-gradient(115deg, rgba(255,255,255,0) 35%, rgba(255,255,255,.5) 48%, rgba(255,255,255,0) 61%); }
.ck-btn:hover:not(:disabled)::after{ transform:translateX(130%); }
.ck-btn:hover:not(:disabled){ background:${RASP_GLOSS}; border-color:#8F0636; transform:translateY(-2px);
  box-shadow:inset 0 2px 2px rgba(255,255,255,.6), inset 0 -4px 6px rgba(0,0,0,.25), 0 7px 0 #8F0636, 0 16px 22px -6px rgba(227,11,92,.5); }
.ck-btn:active:not(:disabled){ transform:translateY(4px); box-shadow:inset 0 2px 2px rgba(255,255,255,.4), inset 0 -3px 5px rgba(0,0,0,.3), 0 1px 0 #8F0636, 0 4px 8px -4px rgba(0,0,0,.5); }
.ck-btn.block{ width:100%; margin-top:20px; height:58px; }

.ck-cap{ font-size:10px; font-weight:600; letter-spacing:.14em; text-transform:uppercase; color:${RASP}; }
.ck-trust{ display:flex; align-items:center; justify-content:center; gap:8px; margin-top:14px; text-align:center; }
.ck-hov{ position:fixed; z-index:600; width:312px; background:#fff; border:2.5px solid ${INK}; border-radius:26px; padding:14px 16px 12px; pointer-events:none;
  box-shadow:0 6px 0 ${INK}, 0 24px 50px -20px rgba(0,0,0,.45); animation:ckDrop .18s ease-out; }
.ck-hov h4{ margin:0 0 6px; font-family:'Cormorant Garamond',serif; font-size:23px; font-weight:700; line-height:1.15; }
.ck-hrow{ display:flex; align-items:center; gap:10px; padding:8px 0; border-top:2px dotted ${LINE}; }
.ck-hph{ width:44px; height:56px; border-radius:12px; overflow:hidden; background:${YEL_SOFT}; border:2px solid ${INK}; flex-shrink:0; display:flex; align-items:center; justify-content:center; }
.ck-hph img{ width:100%; height:100%; object-fit:cover; display:block; }
.ck-hcol{ display:flex; align-items:center; gap:6px; font-size:12px; font-weight:600; letter-spacing:.1em; text-transform:uppercase; }
.ck-hdot{ width:15px; height:15px; border-radius:50%; border:1.5px solid ${INK}; flex-shrink:0; }
.ck-hfoot{ display:flex; align-items:baseline; justify-content:space-between; margin-top:4px; padding-top:10px; border-top:2px solid ${INK}; font-size:12px; letter-spacing:.14em; text-transform:uppercase; }
.ck-hfoot b{ font-family:'Cormorant Garamond',serif; font-size:26px; font-weight:700; letter-spacing:0; }
@media (max-width:768px){ .ck-hov{ display:none; } }

.ck-empty{ display:flex; flex-direction:column; align-items:center; gap:12px; text-align:center; padding:44px 20px; }
.ck-empty-orb{ width:96px; height:96px; border-radius:50%; display:flex; align-items:center; justify-content:center; background:${YEL_GLOSS}; border:2.5px solid ${INK}; box-shadow:inset 0 3px 2px rgba(255,255,255,.8), 0 6px 0 ${INK}; animation:ckBob 2.6s ease-in-out infinite; }
.ck-center{ display:flex; align-items:center; justify-content:center; min-height:100vh; padding:20px; }
.ck-thanks{ text-align:center; max-width:440px; width:100%; padding:38px 24px 30px; }
.ck-thanks .ck-orb{ width:84px; height:84px; margin:0 auto 18px; background:radial-gradient(circle at 30% 24%, #fff 0, rgba(255,255,255,.85) 6%, rgba(255,255,255,0) 26%), radial-gradient(circle at 36% 30%, #eaf8bd 0, ${LIME} 48%, #9bbd4f 100%); color:${INK}; animation:ckBob 2.2s ease-in-out infinite; }
.ck-thanks h2{ justify-content:center; margin:0; }
.ck-thanks p{ margin:10px 0 24px; }
.ck-pop{ position:absolute; z-index:0; pointer-events:none; animation:ckRise 3.6s ease-in infinite; opacity:0; }
.ck-spin{ animation:ckSpin .8s linear infinite; }
@keyframes ckSpin{ to{ transform:rotate(360deg); } }
@keyframes ckDrop{ from{ opacity:0; transform:translateY(-8px) scale(.98); } to{ opacity:1; transform:translateY(0) scale(1); } }
@keyframes ckBob{ 0%,100%{ transform:translateY(0); } 50%{ transform:translateY(-8px); } }
@keyframes ckWiggle{ 0%,100%{ transform:rotate(0); } 25%{ transform:rotate(-14deg); } 75%{ transform:rotate(14deg); } }
@keyframes ckShake{ 0%,100%{ transform:translateX(0); } 25%{ transform:translateX(-5px); } 75%{ transform:translateX(5px); } }
@keyframes ckRise{ 0%{ opacity:0; transform:translateY(40px) scale(.6) rotate(0); } 15%{ opacity:1; } 100%{ opacity:0; transform:translateY(-260px) scale(1.1) rotate(25deg); } }
@media (prefers-reduced-motion: reduce){ .ck-form, .ck-spin, .ck-fl, .ck-pop, .ck-empty-orb, .ck-thanks .ck-orb, .ck-input.bad, .ck-error{ animation:none !important; } .ck-page *{ transition:none !important; } }
`;

/* ═══════════════ PAGE ═══════════════ */
export default function Checkout() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const storeItems = useSelector((s: RootState) => (s as any).cart?.items ?? []);

  /* what the shop handed over; the cart store is only the fallback */
  const fromShop: any[] | undefined = (location.state as any)?.items;
  const [lines, setLines] = useState<Line[]>(() => toLines(fromShop ?? storeItems));
  useEffect(() => { if (!fromShop) setLines(toLines(storeItems)); }, [fromShop, storeItems]);

  const [gifting, setGifting] = useState(false);
  const [gift, setGift] = useState<Gift>(emptyGift());
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const paid = params.get("paid") === "1";

  useEffect(() => { if (paid) dispatch(clearCart()); }, [paid, dispatch]);

  const total = useMemo(() => lines.reduce((s, l) => s + l.price * l.qty, 0), [lines]);
  const count = useMemo(() => lines.reduce((s, l) => s + l.qty, 0), [lines]);

  const products = useSelector((s: any) => s.products?.products) as any[] | undefined;
  useEffect(() => {
    const unsub = dispatch(listenToPublishedProducts() as any) as any;
    return () => { if (typeof unsub === "function") unsub(); };
  }, [dispatch]);

  const [hov, setHov] = useState<{ productId: string; x: number; y: number } | null>(null);
  const productOf = (id: string) => (products ?? []).find((p) => p.productId === id);

  const maxFor = (l: Line): number => {
    const p = productOf(l.productId);
    if (!p) return Infinity;
    const vs: any[] = (p.variants || []).filter((v: any) => v && v.color);
    const v = l.color ? vs.find((x) => x.color === l.color) : undefined;
    let own = Infinity;
    if (v) {
      const rows: any[] = v.stock || [];
      own = l.size
        ? Number(rows.find((s) => s.size === l.size)?.qty) || 0
        : rows.reduce((s, x) => s + (Number(x.qty) || 0), 0);
    }
    const total = Math.max(0, Number(p.quantity) || 0);
    const others = lines.filter((x) => x.productId === l.productId && x.key !== l.key).reduce((s, x) => s + x.qty, 0);
    return Math.max(1, Math.min(own, total - others));
  };

  const bump = (key: string, d: number) =>
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, qty: Math.min(Math.max(1, l.qty + d), maxFor(l)) } : l)));
  const drop = (key: string) => setLines((ls) => ls.filter((l) => l.key !== key));
  const setG = (k: keyof Gift, v: string) => setGift((g) => ({ ...g, [k]: v }));

  /* only the "someone else" form is ever required */
  const giftMissing = {
    firstName: !gift.firstName.trim(), email: !gift.email.trim(), address: !gift.address.trim(),
    city: !gift.city.trim(), country: !gift.country.trim(),
  };
  const giftOk = !Object.values(giftMissing).some(Boolean);

  const pay = async () => {
    setTried(true);
    setError("");
    if (gifting && !giftOk) { setError("Fill in the highlighted details for the person you are buying for."); return; }
    setBusy(true);
    try {
      const u = auth.currentUser;
      if (!u) throw new Error("Please log in first.");
      const idToken = await u.getIdToken();
      const res = await fetch(CHECKOUT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({
          /* "productId:ownerId:n:qty", the server re-prices everything from Firestore */
          itemRefs: lines.map((l, n) => `${l.productId}:${l.ownerId}:${n}:${l.qty}`),
          /* the same lines with the buyer's color and size choice */
          items: lines.map((l) => ({
            productId: l.productId, ownerId: l.ownerId, name: l.name, qty: l.qty,
            ...(l.color ? { color: l.color } : {}),
            ...(l.size ? { size: l.size } : {}),
          })),
          buyerUserId: u.uid,
          /* null = it is for the signed-in buyer; Stripe collects their address */
          giftRecipient: gifting
            ? {
                firstName: gift.firstName.trim(), lastName: gift.lastName.trim(), email: gift.email.trim().toLowerCase(), phone: gift.phone.trim(),
                shippingAddress: {
                  streetAddress: gift.address.trim(), city: gift.city.trim(), state: gift.state.trim(),
                  zipCode: gift.postalCode.trim(), country: gift.country.trim(),
                },
              }
            : null,
          returnUrl: window.location.origin,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.url) throw new Error(data?.error?.message || data?.error || "Couldn't start checkout. Try again.");
      window.location.href = data.url; // Stripe Checkout: cards, Apple Pay, Google Pay
    } catch (e: any) {
      setError(e.message);
      setBusy(false);
    }
  };

  /* ── thank you ── */
  if (paid) {
    return (
      <div className="ck-page ck-center">
        <style>{CSS}</style>
        <Heart className="ck-pop" size={26} fill={PINK} color={INK} style={{ left: "22%", bottom: "18%", animationDelay: "0s" }} />
        <Star className="ck-pop" size={22} fill={YEL} color={INK} style={{ left: "70%", bottom: "14%", animationDelay: "1.1s" }} />
        <Heart className="ck-pop" size={20} fill={RASP} color={INK} style={{ left: "42%", bottom: "10%", animationDelay: "2.2s" }} />
        <Sparkles className="ck-pop" size={24} color={YEL_EDGE} style={{ left: "82%", bottom: "26%", animationDelay: "1.7s" }} />
        <div className="ck-card ck-thanks">
          <div className="ck-orb"><Check size={38} strokeWidth={3} /></div>
          <h2>Thank you! <Heart size={30} fill={PINK} color={INK} /></h2>
          <p className="ck-muted">Your order is confirmed and we are wrapping it up with love.</p>
          <button className="ck-btn" onClick={() => navigate("/orders")}><Sparkles size={15} /> See my orders</button>
        </div>
      </div>
    );
  }

  const field = (k: keyof Gift, label: string, opts: { req?: boolean; full?: boolean; type?: string; ph?: string } = {}) => (
    <div className={opts.full ? "full" : ""}>
      <label className="ck-label">{label}{opts.req && <i>*</i>}</label>
      <input
        className={`ck-input${tried && opts.req && (giftMissing as any)[k] ? " bad" : ""}`}
        type={opts.type || "text"} placeholder={opts.ph} value={gift[k]} onChange={(e) => setG(k, e.target.value)}
      />
    </div>
  );

  return (
    <div className="ck-page">
      <style>{CSS}</style>
      <Sparkles className="ck-fl a" size={34} />
      <Star className="ck-fl b" size={28} fill={YEL} />
      <Heart className="ck-fl c" size={30} fill={PINK} />
      <Sparkles className="ck-fl d" size={26} />
      <div className="ck-wrap">
        <button className="ck-back" onClick={() => navigate("/")}><ArrowLeft size={16} /> Keep shopping</button>
        <h2>Checkout <Sparkles size={30} color={YEL_EDGE} fill={YEL} /></h2>
        <p className="ck-sub"><Heart size={14} fill={RASP} /> Almost yours</p>

        {params.get("cancelled") === "1" && <p className="ck-note"><Heart size={15} fill={PINK} /> Payment cancelled. Your bag is safe.</p>}

        {lines.length === 0 ? (
          <div className="ck-card ck-empty">
            <div className="ck-empty-orb"><ShoppingBag size={42} strokeWidth={1.8} /></div>
            <h3>Your bag is empty</h3>
            <p className="ck-muted">Pick something you love and it will wait for you here.</p>
            <button className="ck-btn" onClick={() => navigate("/")}><Sparkles size={15} /> Start shopping</button>
          </div>
        ) : (
          <>
            <div className="ck-card">
              <div className="ck-card-head">
                <span className="ck-step y">1</span>
                <span className="ck-card-title">Your pretty bag</span>
              </div>
              {lines.map((l) => (
                <div key={l.key} className="ck-line">
                  <div
                    className="ck-thumb"
                    onMouseEnter={(e) => { const r = e.currentTarget.getBoundingClientRect(); setHov({ productId: l.productId, x: r.right + 14, y: r.top }); }}
                    onMouseLeave={() => setHov(null)}
                  >
                    {l.photo ? <img src={l.photo} alt={l.name} /> : <PackageOpen size={22} strokeWidth={1.2} />}
                  </div>
                  <div className="ck-grow">
                    <b className="ck-name">{l.name}</b>
                    {(l.color || l.size) && (
                      <div className="ck-opts">
                        {l.color && <span className="ck-opt">{l.color}</span>}
                        {l.size && <span className="ck-opt">{l.size}</span>}
                      </div>
                    )}
                    <div className="ck-each">{money(l.price)} each</div>
                  </div>
                  <div className="ck-right">
                    <span className="ck-lt">{money(l.price * l.qty)}</span>
                    <div className="ck-qty">
                      <button aria-label="Remove one" disabled={l.qty <= 1} onClick={() => bump(l.key, -1)}><Minus size={14} /></button>
                      <b>{l.qty}</b>
                      <button aria-label="Add one" disabled={l.qty >= maxFor(l)} onClick={() => bump(l.key, 1)}><Plus size={14} /></button>
                    </div>
                    {maxFor(l) !== Infinity && l.qty >= maxFor(l) && <span className="ck-cap">Max {maxFor(l)}</span>}
                    <button className="ck-x" aria-label="Remove" onClick={() => drop(l.key)}><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
            </div>

            <div className="ck-card">
              <div className="ck-card-head">
                <span className="ck-step l">2</span>
                <span className="ck-card-title">Who is it for?</span>
              </div>
              <button type="button" className={`ck-gift${gifting ? " on" : ""}`} aria-pressed={gifting} onClick={() => setGifting((v) => !v)}>
                <span className="ck-orb"><Gift size={20} strokeWidth={2} /></span>
                <span>
                  <b>Buy for someone else</b>
                  <span className="sub">{gifting ? "We will send it to them with love" : "Sending it as a gift? Tell us where"}</span>
                </span>
                <span className="ck-knob" />
              </button>

              {gifting && (
                <div className="ck-form">
                  {field("firstName", "First name", { req: true })}
                  {field("lastName", "Last name")}
                  {field("email", "Their email", { req: true, full: true, type: "email", ph: "them@email.com" })}
                  {field("phone", "Their phone", { full: true, type: "tel", ph: "+372 5000 0000" })}
                  {field("address", "Address", { req: true, full: true, ph: "Street, apartment, building" })}
                  {field("city", "City", { req: true })}
                  {field("postalCode", "Postal code")}
                  {field("state", "State / Province")}
                  {field("country", "Country", { req: true })}
                </div>
              )}
            </div>

            <div className="ck-card">
              <div className="ck-card-head">
                <span className="ck-step">3</span>
                <span className="ck-card-title">Time to pay</span>
              </div>
              <div className="ck-row"><span className="ck-muted">{count} {count === 1 ? "piece" : "pieces"}</span><span>{money(total)}</span></div>
              <div className="ck-total"><small>Total</small><b>{money(total)}</b></div>
              {error && <p className="ck-error">{error}</p>}
              <button className="ck-btn block" onClick={pay} disabled={busy}>
                {busy ? <><Loader2 size={16} className="ck-spin" /> Opening secure checkout</> : <><Lock size={16} /> Pay securely</>}
              </button>
              <p className="ck-muted ck-small ck-trust"><Heart size={13} fill={PINK} color={INK} /> Cards, Apple Pay and Google Pay. Prices are re-checked on our server.</p>
            </div>
          </>
        )}
      </div>

      {hov && (() => {
        const group = lines.filter((x) => x.productId === hov.productId);
        if (!group.length) return null;
        const p = productOf(hov.productId);
        const hexOf = (c?: string): string | null => (p?.variants || []).find((v: any) => v.color === c)?.hex || null;
        const pcs = group.reduce((s, x) => s + x.qty, 0);
        const sum = group.reduce((s, x) => s + x.price * x.qty, 0);
        const left = Math.min(hov.x, window.innerWidth - 324);
        const top = Math.max(12, Math.min(hov.y, window.innerHeight - (104 + group.length * 72) - 12));
        return (
          <div className="ck-hov" style={{ left, top }}>
            <h4>{group[0].name}</h4>
            {group.map((x) => (
              <div key={x.key} className="ck-hrow">
                <div className="ck-hph">{x.photo ? <img src={x.photo} alt="" /> : <PackageOpen size={16} strokeWidth={1.2} color="#6B6B6B" />}</div>
                <div className="ck-grow">
                  <div className="ck-hcol">
                    {hexOf(x.color) && <i className="ck-hdot" style={{ background: hexOf(x.color) as string }} />}
                    {x.color || "Default"}{x.size ? ` · ${x.size}` : ""}
                  </div>
                  <div className="ck-small ck-muted">{x.qty} × {money(x.price)}</div>
                </div>
                <b>{money(x.price * x.qty)}</b>
              </div>
            ))}
            <div className="ck-hfoot"><span>{pcs} {pcs === 1 ? "piece" : "pieces"}</span><b>{money(sum)}</b></div>
          </div>
        );
      })()}
    </div>
  );
}